/**
 * AssumptionsPanel — sliders grouped by category for scenario modeling.
 * Loads catalog from API, renders sliders with min/max/step/default.
 */

import { useCallback } from 'react'
import { cn } from '../../lib/utils'
import { RotateCcw } from 'lucide-react'
import type { Assumptions } from '../../services/projectionEngine'

interface CatalogEntry {
  assumption_key: string
  display_name: string
  category: string
  unit: string
  default_value: number
  min_value: number
  max_value: number
  step: number
  formula_impact: string
  display_order: number
}

interface AssumptionsPanelProps {
  assumptions: Assumptions
  catalog: CatalogEntry[]
  onUpdate: (key: keyof Assumptions, value: number) => void
  onReset: () => void
  className?: string
}

const CATEGORY_ORDER = ['REVENUE', 'COST', 'WORKFORCE', 'WORKING_CAPITAL', 'OTHER']

const CATEGORY_LABELS: Record<string, string> = {
  REVENUE: 'Revenue',
  COST: 'Cost Structure',
  WORKFORCE: 'Workforce',
  WORKING_CAPITAL: 'Working Capital',
  OTHER: 'Other',
}

function formatValue(value: number, unit: string): string {
  if (unit === '%') return `${value.toFixed(1)}%`
  if (unit === '$') return `$${value.toLocaleString()}`
  if (unit === 'days') return `${value} days`
  return value.toLocaleString()
}

export function AssumptionsPanel({
  assumptions, catalog, onUpdate, onReset, className,
}: AssumptionsPanelProps) {
  // Group by category
  const grouped = CATEGORY_ORDER.map(cat => ({
    category: cat,
    label: CATEGORY_LABELS[cat] || cat,
    items: catalog
      .filter(c => c.category === cat)
      .sort((a, b) => a.display_order - b.display_order),
  })).filter(g => g.items.length > 0)

  // If catalog is empty, show fallback with hardcoded entries
  const hasItems = grouped.length > 0

  return (
    <div className={cn('flex flex-col gap-4', className)}>
      <div className="flex items-center justify-between mb-1">
        <h3 className="text-[10px] uppercase tracking-[0.8px] text-[var(--text-muted,#64748B)] flex items-center gap-1.5">
          <span className="w-1.5 h-1.5 rounded-full bg-[var(--accent,var(--accent))]" />
          Assumptions
        </h3>
        <button
          onClick={onReset}
          className={cn(
            'flex items-center gap-1 text-[10px] text-[var(--text-hint,#475569)]',
            'hover:text-[var(--text-secondary,#94A3B8)] transition-colors',
          )}
        >
          <RotateCcw className="h-3 w-3" />
          Reset
        </button>
      </div>

      {hasItems ? (
        grouped.map(({ category, label, items }) => (
          <div key={category}>
            <p className="text-[9px] uppercase tracking-[0.8px] text-[var(--text-hint,#475569)] mb-2">
              {label}
            </p>
            <div className="flex flex-col gap-3">
              {items.map(entry => (
                <SliderRow
                  key={entry.assumption_key}
                  entry={entry}
                  value={(assumptions as any)[entry.assumption_key] ?? entry.default_value}
                  onUpdate={onUpdate}
                />
              ))}
            </div>
          </div>
        ))
      ) : (
        <FallbackSliders assumptions={assumptions} onUpdate={onUpdate} />
      )}
    </div>
  )
}

function SliderRow({
  entry, value, onUpdate,
}: {
  entry: CatalogEntry
  value: number
  onUpdate: (key: keyof Assumptions, value: number) => void
}) {
  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    onUpdate(entry.assumption_key as keyof Assumptions, parseFloat(e.target.value))
  }, [entry.assumption_key, onUpdate])

  const handleInput = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const v = parseFloat(e.target.value)
    if (!isNaN(v)) onUpdate(entry.assumption_key as keyof Assumptions, v)
  }, [entry.assumption_key, onUpdate])

  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center justify-between">
        <label className="text-[11px] text-[var(--text-secondary,#94A3B8)]">
          {entry.display_name}
        </label>
        <input
          type="number"
          value={value}
          step={entry.step}
          onChange={handleInput}
          className={cn(
            'w-20 text-right text-[11px] bg-transparent border-b border-white/10',
            'text-[var(--text-primary,#F0F2F5)] font-mono',
            'focus:outline-none focus:border-[var(--accent,var(--accent))]',
          )}
        />
      </div>
      <input
        type="range"
        min={entry.min_value}
        max={entry.max_value}
        step={entry.step}
        value={value}
        onChange={handleChange}
        className="w-full h-1 appearance-none bg-white/10 rounded-full cursor-pointer accent-[var(--accent)]"
      />
      {entry.formula_impact && (
        <p className="text-[9px] text-[var(--text-hint,#475569)]">
          {entry.formula_impact}
        </p>
      )}
    </div>
  )
}

/** Fallback sliders when catalog API is unavailable */
function FallbackSliders({
  assumptions, onUpdate,
}: {
  assumptions: Assumptions
  onUpdate: (key: keyof Assumptions, value: number) => void
}) {
  const items: { key: keyof Assumptions; label: string; min: number; max: number; step: number; unit: string }[] = [
    { key: 'revenue_growth_rate', label: 'Revenue Growth', min: -20, max: 50, step: 0.5, unit: '%' },
    { key: 'cogs_pct_of_revenue', label: 'COGS % of Revenue', min: 20, max: 90, step: 0.5, unit: '%' },
    { key: 'opex_growth_rate', label: 'OpEx Growth', min: -10, max: 30, step: 0.5, unit: '%' },
    { key: 'headcount_change', label: 'Headcount Change', min: -50, max: 100, step: 1, unit: '' },
    { key: 'collection_days', label: 'Collection Days', min: 15, max: 120, step: 1, unit: 'days' },
    { key: 'payment_terms', label: 'Payment Terms', min: 10, max: 90, step: 1, unit: 'days' },
    { key: 'tax_rate', label: 'Tax Rate', min: 0, max: 40, step: 0.5, unit: '%' },
    { key: 'capex', label: 'CapEx', min: 0, max: 5000000, step: 50000, unit: '$' },
  ]

  return (
    <div className="flex flex-col gap-3">
      {items.map(item => (
        <div key={item.key} className="flex flex-col gap-1">
          <div className="flex items-center justify-between">
            <label className="text-[11px] text-[var(--text-secondary,#94A3B8)]">{item.label}</label>
            <span className="text-[11px] font-mono text-[var(--text-primary,#F0F2F5)]">
              {formatValue(assumptions[item.key], item.unit)}
            </span>
          </div>
          <input
            type="range"
            min={item.min}
            max={item.max}
            step={item.step}
            value={assumptions[item.key]}
            onChange={e => onUpdate(item.key, parseFloat(e.target.value))}
            className="w-full h-1 appearance-none bg-white/10 rounded-full cursor-pointer accent-[var(--accent)]"
          />
        </div>
      ))}
    </div>
  )
}
